import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import { eventService } from './eventService';
import { eventVisitorService } from './eventVisitorService';
import { getActionLogoPng } from '@/utils/imageUtils';

// Add logo and date at the top
const addHeader = async (doc, event) => {
    // Add logo on the left
    const logo = await getActionLogoPng();
    doc.addImage(logo, 'PNG', 20, 20, 50, 20);

    // Add date on the right with Brisbane timezone
    doc.setFontSize(12);
    doc.setTextColor(0, 0, 0);
    const dateStr = new Date(event.event_date).toLocaleString('en-AU', {
        timeZone: 'Australia/Brisbane',
        weekday: 'short',
        day: 'numeric',
        month: 'short',
        year: 'numeric'
    }).replace(/,/g, ''); // Remove commas

    const textWidth = doc.getTextWidth(dateStr);
    doc.text(dateStr, doc.internal.pageSize.width - 20 - textWidth, 30);

    // Add Agenda title
    doc.setFontSize(24);
    doc.text('Agenda', doc.internal.pageSize.width / 2, 50, { align: 'center' });
};

export const generateAgendaPDF = async (event, members) => {
    // Get visitors the same way EventDetailsModal does
    const visitors = await eventVisitorService.getEventVisitors(event.id);
    
    const doc = new jsPDF();
    
    // Add header with logo and date
    await addHeader(doc, event);

    // Helper function to get speaker name
    const getSpeakerName = (session, members) => {
        if (!session?.speaker_id) return 'TBA';
        const speaker = members.find(m => m.id === session.speaker_id);
        return speaker ? `${speaker.first_name} ${speaker.last_name}` : 'TBA';
    };

    // Add agenda table starting lower to accommodate header
    doc.autoTable({
        head: [['Time', 'Activity']],
        body: [
            ['6:45 AM', 'Arrival'],
            ['7:00 AM', 'Welcome, Celebrations, Introduce Substitutes and Visitors'],
            ['7:05 AM', `Member Pitch: ${event.intro_topic || 'TBA'}`],
            ...(event.has_education_session ? [
                ['7:20 AM', [
                    `Education Session: ${getSpeakerName(event.education_session, members)} - ${event.education_session?.topic || 'TBA'}`,
                    event.education_session?.description || ''
                ].filter(Boolean).join('\n')],
                ['7:35 AM', [
                    `Member Presentation: ${getSpeakerName(event.main_session, members)} - ${event.main_session?.topic || 'TBA'}`,
                    event.main_session?.description || ''
                ].filter(Boolean).join('\n')]
            ] : [
                ['7:20 AM', [
                    `Member Presentation: ${getSpeakerName(event.main_session, members)} - ${event.main_session?.topic || 'TBA'}`,
                    event.main_session?.description || ''
                ].filter(Boolean).join('\n')]
            ])
        ],
        startY: 60,
        theme: 'grid',
        headStyles: {
            fillColor: [66, 133, 244],
            textColor: 255,
            fontSize: 10
        },
        styles: {
            cellPadding: 3,
            fontSize: 10,
            lineHeight: 1.2
        },
        columnStyles: {
            0: { cellWidth: 30 },
            1: { cellWidth: 'auto' }
        }
    });

    // Get next meeting info
    try {
        const nextMeeting = await eventService.getNextMeeting(event.id);
        
        if (nextMeeting) {
            const nextMeetingDate = new Date(nextMeeting.event_date).toLocaleString('en-AU', {
                timeZone: 'Australia/Brisbane',
                weekday: 'long',
                day: 'numeric',
                month: 'long',
                year: 'numeric'
            });
            
            // Get the Y position for the next table
            const startY = doc.lastAutoTable ? doc.lastAutoTable.finalY + 10 : 60;
            
            doc.autoTable({
                head: [['Next Meeting - ' + nextMeetingDate]],
                body: [
                    [nextMeeting.education_session ? 
                        `Education: ${nextMeeting.education_session.speaker} - ${nextMeeting.education_session.topic}` : ''],
                    [nextMeeting.member_presentation ? 
                        `Member Presentation: ${nextMeeting.member_presentation.speaker} - ${nextMeeting.member_presentation.topic}` : ''],
                    [`Member Pitch: ${nextMeeting.intro_topic || 'TBA'}`]
                ].filter(row => row[0]), // Remove empty rows
                startY: startY,
                theme: 'grid',
                headStyles: {
                    fillColor: [66, 133, 244],
                    textColor: 255,
                    fontSize: 10
                },
                styles: {
                    fontSize: 10,
                    cellPadding: 5
                }
            });
        }
    } catch (error) {
        // Continue without next meeting info
    }
    
    // Add members table - with safer variable handling
    try {
        // We already have members passed to the function, no need to fetch again
        
        doc.addPage();
        
        // Define column widths
        const nameWidth = 35;
        const businessWidth = 45;
        const notesWidth = 90;
        
        // Add simple title instead of table header
        doc.setFontSize(12);
        doc.setFont(undefined, 'bold');
        doc.text('Member List', 20, 20);
        doc.setFont(undefined, 'normal');

        // Member details table - with null checks
        if (members && Array.isArray(members)) {
            doc.autoTable({
                head: [['Name', 'Business', 'Notes']],
                body: members.map(m => [
                    `${m?.first_name || ''} ${m?.last_name || ''}`,
                    m?.company_name || '',
                    ''
                ]),
                startY: 25,
                theme: 'grid',
                headStyles: {
                    fillColor: [66, 133, 244],
                    textColor: 255,
                    fontSize: 8,
                    cellPadding: 2,
                    minCellHeight: 3
                },
                styles: {
                    fontSize: 8,
                    cellPadding: 2,
                    minCellHeight: 2
                },
                columnStyles: {
                    0: { cellWidth: nameWidth },
                    1: { cellWidth: businessWidth },
                    2: { cellWidth: notesWidth }
                }
            });
        }
    } catch (error) {
        // Continue with the rest of the PDF
    }

    // Visitor list with matching compact styling
    if (visitors?.length > 0) {
        doc.setFontSize(12);
        doc.setFont(undefined, 'bold');
        
        // Define column widths
        const nameWidth = 40;
        const businessWidth = 50;
        const notesWidth = 40;
        
        // Calculate the Y position safely
        const visitorTitleY = doc.lastAutoTable ? doc.lastAutoTable.finalY + 15 : doc.internal.pageSize.height - 100;
        doc.text('Visitor List', 20, visitorTitleY);
        doc.setFont(undefined, 'normal');

        doc.autoTable({
            head: [['Name', 'Business', 'Notes']],
            body: visitors.map(v => [
                `${v.first_name} ${v.last_name}`,
                v.company || '',
                ''
            ]),
            startY: doc.lastAutoTable ? doc.lastAutoTable.finalY + 20 : visitorTitleY + 5,
            theme: 'grid',
            headStyles: {
                fillColor: [66, 133, 244],
                textColor: 255,
                fontSize: 8,
                cellPadding: 2,
                minCellHeight: 4
            },
            styles: {
                fontSize: 8,
                cellPadding: 2,
                minCellHeight: 3
            },
            columnStyles: {
                0: { cellWidth: nameWidth },
                1: { cellWidth: businessWidth },
                2: { cellWidth: notesWidth }
            }
        });
    }
    
    // Format date for filename using local timezone
    const fileDate = new Date(event.event_date).toLocaleDateString('en-AU', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
    }).split('/').reverse().join('-');
    
    doc.save(`Action Network Agenda ${fileDate}.pdf`);
}; 